import { dailyValue } from "./daily-values";
import type { Market, NutrientId, Profile } from "./types";

/**
 * Round `value` to the nearest multiple of `increment`, breaking exact midpoints
 * **half-away-from-zero** (the FDA convention — 21 CFR 101.9), NOT banker's rounding.
 *
 * The `+ 1e-9` epsilon makes the half-up tiebreak floating-point-safe (e.g. 9.5 stored
 * as 9.499999999999998 still rounds to 10). Result is renormalized via `toPrecision(12)`
 * to shed binary-float dust like 2.5000000000000004.
 */
export function roundToIncrement(value: number, increment: number): number {
  if (increment <= 0) return value;
  const q = value / increment;
  const rounded = Math.sign(q) * Math.round(Math.abs(q) + 1e-9);
  return Number((rounded * increment).toPrecision(12));
}

type RuleId = "R_CAL" | "R_FAT" | "R_CHOL" | "R_SODIUM" | "R_CARB";

/**
 * Which display-rounding rule each nutrient's *quantitative amount* uses (21 CFR 101.9(c)).
 *
 * NOTE: the four micros (vitamin D / calcium / iron / potassium) carry an `R_CARB`
 * placeholder here, but their amounts are rendered by `displayMicroAmount` (A4), never by
 * `displayAmount`. `displayPercentDV` (A3) is independent of this table.
 */
const US_RULE: Record<NutrientId, RuleId> = {
  energy: "R_CAL",
  fat_total: "R_FAT",
  fat_sat: "R_FAT",
  fat_trans: "R_FAT",
  cholesterol: "R_CHOL",
  sodium: "R_SODIUM",
  carb_total: "R_CARB",
  fiber: "R_CARB",
  sugars_total: "R_CARB",
  sugars_added: "R_CARB",
  protein: "R_CARB",
  vitamin_d: "R_CARB",
  calcium: "R_CARB",
  iron: "R_CARB",
  potassium: "R_CARB",
};

function applyUsRule(rule: RuleId, v: number): string {
  switch (rule) {
    case "R_CAL":
      // (c)(1): <5 -> 0; 5..50 -> nearest 5; >50 -> nearest 10.
      return v < 5 ? "0" : String(roundToIncrement(v, v <= 50 ? 5 : 10));
    case "R_FAT":
      // (c)(2): <0.5 -> 0; 0.5..<5 -> nearest 0.5; >=5 -> nearest 1.
      return v < 0.5 ? "0" : String(roundToIncrement(v, v < 5 ? 0.5 : 1));
    case "R_CHOL":
      // (c)(3): <2 -> 0; 2..<5 -> "less than 5 mg"; >=5 -> nearest 5.
      return v < 2 ? "0" : v < 5 ? "less than 5 mg" : String(roundToIncrement(v, 5));
    case "R_SODIUM":
      // (c)(4): <5 -> 0; 5..140 -> nearest 5; >140 -> nearest 10.
      return v < 5 ? "0" : String(roundToIncrement(v, v <= 140 ? 5 : 10));
    case "R_CARB":
      // (c)(6)/(c)(7): <0.5 -> 0; 0.5..<1 -> "less than 1 g"; >=1 -> nearest 1.
      return v < 0.5 ? "0" : v < 1 ? "less than 1 g" : String(roundToIncrement(v, 1));
  }
}

/**
 * Display the quantitative amount for a nutrient+market as a regulation-correct STRING
 * (e.g. "0", "2.5", "less than 1 g", "135"). Operates on the UNROUNDED analytical value.
 */
export function displayAmount(id: NutrientId, market: Market, unrounded: number): string {
  if (market !== "US") {
    throw new Error(`market ${market} not implemented in M1`); // EU/CA/UK = M-Launch/M-Growth
  }
  return applyUsRule(US_RULE[id], unrounded);
}

/**
 * The four micros that use the 2/5/10 %DV grid (`R_PDV`, 21 CFR 101.9(c)(8)(iii)).
 * Every other DV-bearing nutrient (the macros with a DRV) uses `R_PDV_MACRO` — nearest
 * whole 1%. Keeping the two grids separate is a documented bug source.
 */
const MICRO: ReadonlySet<NutrientId> = new Set([
  "vitamin_d",
  "calcium",
  "iron",
  "potassium",
]);

/**
 * %DV display string for a nutrient, or `null` when no DV is established OR (for micros)
 * when the value rounds below the 2% reporting threshold.
 *
 * CRITICAL: `%DV` is computed from the **UNROUNDED** analytical amount, not the rounded
 * display amount (21 CFR 101.9(c)(8)(iii) — factors apply to the actual amount). Proof:
 * sat fat 4.75 g -> 4.75/20 = 23.75% -> "24%", NOT 5/20 = 25%.
 *
 * - Micros (R_PDV): <2% -> null (omit/footnote); ≤10% -> nearest 2; >10..50% -> nearest 5;
 *   >50% -> nearest 10.
 * - Macros with a DRV (R_PDV_MACRO): nearest whole 1% (no <2% suppression).
 *
 * Whether a row actually *shows* its %DV in the assembled panel is decided separately by
 * the nutrient model's `showPercentDV` flag (e.g. protein's %DV is suppressed by default),
 * not here.
 */
export function displayPercentDV(
  id: NutrientId,
  market: Market,
  profile: Profile,
  unroundedAmount: number,
): string | null {
  if (market !== "US") {
    throw new Error(`market ${market} not implemented in M1`); // EU/CA/UK = M-Launch/M-Growth
  }
  const dv = dailyValue(id, market, profile);
  if (dv == null) return null;
  const p = (unroundedAmount / dv) * 100;
  if (MICRO.has(id)) {
    if (p < 2) return null; // below the reporting threshold -> omit / footnote
    const inc = p <= 10 ? 2 : p <= 50 ? 5 : 10;
    return `${roundToIncrement(p, inc)}%`;
  }
  return `${roundToIncrement(p, 1)}%`; // macro: nearest whole 1%
}
